import React, { useEffect, useState } from "react";
import { apiGet, apiPost } from "../../../services/api";
import Button from "../../common/Button";
import "./SurgeryQueueWidget.css";

interface SurgeryCase {
    procedure_id: number;
    patient_id: string | number;
    visit_id: number | string;
    patient_name?: string;
    tooth?: string;
    phone?: string;
    consultant?: string;
    status?: string;
    procedure_type?: string;
}

interface SurgeryQueueWidgetProps {
    onSelectPatient?: ((_patientId: number, _visitId: number) => void) | null;
    groupByPatient?: boolean;
}

type SurgeryDisplayCase = SurgeryCase & {
    key: string;
    teeth?: string[];
    consultants?: string[];
};

const splitTeeth = (raw?: string): string[] => {
    if (!raw) return [];
    return raw
        .split(/[,\s]+/)
        .map((t) => t.trim())
        .filter(Boolean);
};

export default function SurgeryQueueWidget({ onSelectPatient, groupByPatient = false }: SurgeryQueueWidgetProps) {
    const [ongoingCases, setOngoingCases] = useState<SurgeryCase[]>([]);
    const [loading, setLoading] = useState(false);

    const loadCases = async () => {
        setLoading(true);
        try {
            const data = await apiGet("/api/doctor/surgery/ongoing") as SurgeryCase[];
            setOngoingCases(Array.isArray(data) ? data : []);
        } catch (e) {
            console.error("Failed to load Surgery queue", e);
        } finally {
            setLoading(false);
        }
    };

    const updateStatus = async (procedureId: number, status: string) => {
        try {
            await apiPost(`/api/doctor/procedures/${procedureId}/status`, { status });
            loadCases();
        } catch {
            console.error("Failed to update status.");
        }
    };

    useEffect(() => {
        loadCases();
        const interval = setInterval(loadCases, 30000);
        return () => clearInterval(interval);
    }, []);

    const displayCases = React.useMemo<SurgeryDisplayCase[]>(() => {
        if (!groupByPatient) {
            return ongoingCases.map((item) => ({
                ...item,
                key: String(item.procedure_id),
                teeth: item.tooth ? splitTeeth(item.tooth) : []
            }));
        }

        const grouped = new Map<string, SurgeryDisplayCase>();
        ongoingCases.forEach((item) => {
            const key = `${item.patient_id ?? ""}`;
            const existing = grouped.get(key);
            const teeth = splitTeeth(item.tooth);
            const consultant = (item.consultant || "").trim();

            if (existing) {
                teeth.forEach((t) => existing.teeth?.push(t));
                if (consultant && existing.consultants && !existing.consultants.includes(consultant)) {
                    existing.consultants.push(consultant);
                }
                if (!existing.status && item.status) existing.status = item.status;
            } else {
                grouped.set(key, {
                    ...item,
                    key,
                    teeth: [...teeth],
                    consultants: consultant ? [consultant] : []
                });
            }
        });

        return Array.from(grouped.values()).map((item) => {
            const uniqueTeeth = Array.from(new Set(item.teeth || []));
            const uniqueConsultants = Array.from(new Set(item.consultants || []));
            return {
                ...item,
                teeth: uniqueTeeth,
                consultants: uniqueConsultants
            };
        });
    }, [groupByPatient, ongoingCases]);

    return (
        <div className="surgery-queue-window" style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div className="queue-header" style={{ marginBottom: '10px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0 10px' }}>
                <h3 style={{ margin: 0, color: '#444' }}>🔩 Surgery & Implant Active</h3>
                <button className="refresh-btn" type="button" onClick={loadCases} style={{ padding: '6px 12px', borderRadius: '20px', border: 'none', background: '#f0f0f0', cursor: 'pointer' }}>↻</button>
            </div>

            <div className="queue-list" style={{ flex: 1, overflowY: 'auto', padding: '10px 5px' }}>
                {loading && <div className="loading-row" style={{ textAlign: 'center', padding: '20px', color: '#888' }}>Loading cases...</div>}

                {!loading && displayCases.length === 0 && (
                    <div className="empty-row" style={{ textAlign: 'center', padding: '40px', color: '#999', background: '#f8f9fa', borderRadius: '8px' }}>
                        <h4>No active surgeries</h4>
                        <p>New extraction, implant, or surgery procedures started in Doctor module will appear here.</p>
                    </div>
                )}

                {displayCases.map((item) => (
                    <div
                        key={item.key}
                        className="queue-card"
                        onClick={() => onSelectPatient?.(Number(item.patient_id), Number(item.visit_id))}
                        role="button"
                        tabIndex={0}
                        style={{
                            background: 'white',
                            borderRadius: '12px',
                            padding: '16px',
                            marginBottom: '12px',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                            borderLeft: '4px solid #ef4444', // Red for surgery
                            cursor: 'pointer',
                            transition: 'transform 0.2s, box-shadow 0.2s'
                        }}
                        onMouseOver={(e) => {
                            e.currentTarget.style.transform = 'translateY(-2px)';
                            e.currentTarget.style.boxShadow = '0 8px 16px rgba(0,0,0,0.1)';
                        }}
                        onMouseOut={(e) => {
                            e.currentTarget.style.transform = 'translateY(0)';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.05)';
                        }}
                    >
                        <div className="q-card-top" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                            <span className="q-name" style={{ fontSize: '1.05rem', fontWeight: 600, color: '#333' }}>
                                {item.patient_name}
                                <span style={{ fontSize: "0.80em", fontWeight: "normal", color: "#666", marginLeft: "8px", background: '#f3f4f6', padding: '2px 6px', borderRadius: '4px' }}>
                                    {item.procedure_type || "Surgery"}
                                </span>
                            </span>
                            <span className="q-tooth" style={{ background: '#fee2e2', color: '#b91c1c', padding: '2px 8px', borderRadius: '6px', fontWeight: 700 }}>
                                Tooth: {item.teeth && item.teeth.length > 0 ? item.teeth.join(", ") : (item.tooth || "N/A")}
                            </span>
                        </div>

                        {(item.consultants && item.consultants.length > 0) || item.consultant ? (
                            <div style={{ fontSize: '0.85rem', color: '#555', marginBottom: '6px' }}>
                                👨‍⚕️ Dr. {item.consultants ? item.consultants.join(", ") : item.consultant}
                            </div>
                        ) : null}

                        <div className="q-card-bottom" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.9rem', marginTop: '12px' }}>
                            <span className="q-phone" style={{ color: '#666' }}>📞 {item.phone}</span>
                            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                                <span className="q-status" style={{
                                    color: item.status === 'Completed' ? '#166534' : '#d97706',
                                    fontWeight: 500,
                                    background: item.status === 'Completed' ? '#dcfce7' : '#fef3c7',
                                    padding: '2px 8px',
                                    borderRadius: '12px',
                                    marginRight: '8px'
                                }}>
                                    {item.status || "In Progress"}
                                </span>
                                {(item.status !== "Completed") && (
                                    <>
                                        <Button variant="secondary" size="sm" onClick={(e) => { e.stopPropagation(); updateStatus(item.procedure_id, "In Progress"); }} style={{ padding: '4px 10px', fontSize: '0.8rem' }}>Start</Button>
                                        <Button variant="primary" size="sm" onClick={(e) => { e.stopPropagation(); updateStatus(item.procedure_id, "Completed"); }} style={{ padding: '4px 10px', fontSize: '0.8rem', background: '#16a34a', borderColor: '#16a34a' }}>Finish</Button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
}
